import React, { useState, useEffect, useRef } from "react";
import Modal from "react-bootstrap/Modal";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { toast } from "react-toastify";
import dateFormat from "dateformat";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import { useTheme } from "../../../context/ThemeContext";
import { editInvoice } from "../../../models/InvoiceModel";
import { apiError } from "../../../helpers/ErrorHelper";

const UpdateInvoiceModal = ({
  showModal,
  setShowModal,
  modalKey,
  invoiceData,
  setInvoiceData,
}) => {
  const { state } = useAuth();
  const { theme, selectBoxTheme } = useTheme();
  const navigate = useNavigate();
  const loader = document.getElementById("loading");

  let user_data = state.user_data;

  const [loading, setLoading] = useState(false);

  const [invoice_bill_date, setInvoiceBillDate] = useState("");
  const [invoice_due_date, setInvoiceDueDate] = useState("");
  const [invoice_month, setInvoiceMonth] = useState("");
  const [invoice_po_number, setInvoicePONumber] = useState("");

  const [errors, setErrors] = useState({
    invoice_bill_date: "",
    invoice_due_date: "",
    invoice_month: "",
    invoice_po_number: "",
  });

  useEffect(() => {
    if (invoiceData) {
      setInvoiceBillDate(
        invoiceData.invoice_bill_date
          ? new Date(invoiceData.invoice_bill_date)
          : ""
      );
      setInvoiceDueDate(
        invoiceData.invoice_due_date
          ? new Date(invoiceData.invoice_due_date)
          : ""
      );
      setInvoiceMonth(
        invoiceData.invoice_month ? new Date(invoiceData.invoice_month) : ""
      );
      setInvoicePONumber(invoiceData.invoice_po_number || "");
      setErrors({});
    }
  }, [invoiceData, modalKey]);

  const closeModal = () => {
    setInvoiceBillDate("");
    setInvoiceDueDate("");
    setInvoiceMonth("");
    setInvoicePONumber("");
    setErrors({});
    setShowModal(false);
  };

  const updateInvoice = async () => {
    loader.style.display = "block";
    setLoading(true);

    try {
      setErrors({});

      const formData = {
        //invoice_unique_id: invoiceData.invoice_unique_id,
        invoice_bill_date: invoice_bill_date
          ? dateFormat(invoice_bill_date, "dd-mm-yyyy")
          : "",
        invoice_due_date: invoice_due_date
          ? dateFormat(invoice_due_date, "dd-mm-yyyy")
          : "",
        invoice_month: invoice_month
          ? dateFormat(invoice_month, "mmm-yyyy")
          : "",
        invoice_po_number: invoice_po_number,
      };

      const response = await editInvoice(
        user_data.user_token,
        invoiceData.invoice_unique_id,
        formData
      );

      if (response.status) {
        toast.success("Invoice details updated successfully!");
        // ✅ Update with API response instead of local formData
        if (response.data.invoice_data) {
            setInvoiceData(response.data.invoice_data);
        }
        closeModal();
      } else {
        setErrors(response.error || {});
        toast.error(response.message || "Failed to update invoice details");
      }
    } catch (error) {
      apiError(error);
      if (error.response?.data?.invalid_token === 1) {
        navigate("/re-login");
      }
    }

    loader.style.display = "none";
    setLoading(false);
  };

  return (
    <Modal key={modalKey} show={showModal} onHide={closeModal} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Update Invoice Details</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="row gy-2">
          <div className="col-xl-6">
            <label className="form-label">
              Bill Date <span className="text-danger">*</span>
            </label>
            <DatePicker
              className="form-control"
              placeholderText="DD-MM-YYYY"
              selected={invoice_bill_date}
              onChange={setInvoiceBillDate}
              dateFormat="dd-MM-yyyy"
              maxDate={invoice_due_date}
            />
            {errors.invoice_bill_date && (
              <p className="text-danger">{errors.invoice_bill_date}</p>
            )}
          </div>

          <div className="col-xl-6">
            <label className="form-label">
              Due Date <span className="text-danger">*</span>
            </label>
            <DatePicker
              className="form-control"
              placeholderText="DD-MM-YYYY"
              selected={invoice_due_date}
              onChange={setInvoiceDueDate}
              dateFormat="dd-MM-yyyy"
              minDate={invoice_bill_date || new Date()}
              disabled={!invoice_bill_date}
            />
            {errors.invoice_due_date && (
              <p className="text-danger">{errors.invoice_due_date}</p>
            )}
          </div>

          <div className="col-xl-6">
            <label className="form-label">
              Invoice Month <span className="text-danger">*</span>
            </label>
            <DatePicker
              className="form-control"
              placeholderText="MMM-YYYY"
              selected={invoice_month}
              onChange={setInvoiceMonth}
              showMonthYearPicker
              dateFormat="MMM-yyyy"
              maxDate={invoice_due_date}
            />
            {errors.invoice_month && (
              <p className="text-danger">{errors.invoice_month}</p>
            )}
          </div>

          <div className="col-xl-6">
            <label className="form-label">
              PO Number <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className="form-control"
              placeholder="PO Number"
              value={invoice_po_number}
              onChange={(e) => setInvoicePONumber(e.target.value)}
            />
            {errors.invoice_po_number && (
              <p className="text-danger">{errors.invoice_po_number}</p>
            )}
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <button type="button" className="btn btn-light" onClick={closeModal}>
          Cancel
        </button>
        <button type="button" className="btn btn-primary" onClick={updateInvoice}>
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm align-middle"></span>
              Loading...
            </>
          ) : (
            "Save Changes"
          )}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default UpdateInvoiceModal;